import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import {
  Box,
  DataTable,
  Flex,
  Progress,
  SettingsRow,
  SettingsSection,
  Spinner,
  TagBadge,
  Text,
} from '@chatsdet/ui';
import { encodePrefixedId } from '@chatsdet/ids';
import type { AgentTimeRow, BillingSummary } from '@chatsdet/types';
import { useAgentTime, useBillingSummary } from '../../../hooks/useBillingData';
import { billingMeters, formatDay, isEmptyPeriod, type Meter } from '../../../hooks/billingMeters';

const formatNumber = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

const CATEGORY_LABELS: Record<string, string> = {
  flow: 'Flow runs',
  playwright: 'Playwright tests',
  healing: 'Healing',
  agent_testing: 'Agent testing',
  diagnosis: 'Diagnosis',
};
const categoryLabel = (category: string) => CATEGORY_LABELS[category] ?? category;

/** `1h 05m`, `12m 30s`, `45s`. */
function formatSeconds(total: number): string {
  const seconds = Math.round(total);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Where a ledger subject lives in the app; null when it has no page of its own. */
function subjectHref(row: AgentTimeRow): string | null {
  if (!row.subjectType || !row.subjectId) return null;
  switch (row.subjectType) {
    case 'workflow_run':
      return `/runs/${row.subjectId}`;
    case 'adhoc_test':
      return `/exploratory/${encodePrefixedId('adhoc', row.subjectId)}`;
    case 'pr_review':
      return `/pr-reviews/${row.subjectId}`;
    case 'failure_diagnostic':
    case 'durable_agent':
      return null;
  }
}

function planLabel(summary: BillingSummary): string {
  if (summary.plan === 'trial') return 'Trial';
  if (summary.plan === 'starter') return 'Starter';
  return 'Contract';
}

function UsageMeter({
  title,
  meter,
  unit,
  pendingLabel,
  pending,
  breakdown,
}: {
  title: string;
  meter: Meter;
  unit: string;
  pendingLabel: string;
  pending: number;
  breakdown: Array<{ category: string; value: number }>;
}) {
  const variant = meter.level === 'full' ? 'destructive' : meter.level === 'warning' ? 'warning' : 'default';
  return (
    <Flex direction="column" gap="sm">
      <Flex justify="between" align="baseline" gap="md">
        <Text weight="medium">{title}</Text>
        <Text size="sm" className="tabular-nums text-muted-foreground">
          {formatNumber(meter.counted)}
          {meter.included === null ? ` ${unit} · no limit` : ` / ${formatNumber(meter.included)} ${unit}`}
        </Text>
      </Flex>
      {meter.percent !== null && (
        <Progress value={Math.min(meter.percent, 100) / 100} variant={variant} aria-label={`${title} used`} />
      )}
      {meter.level !== 'ok' && (
        <Text size="sm" className={meter.level === 'full' ? 'text-destructive' : 'text-warning'}>
          {meter.level === 'full' ? 'Limit reached.' : 'Over 80% used.'}
        </Text>
      )}
      <Text size="sm" className="tabular-nums text-muted-foreground">
        {breakdown.map((b) => `${categoryLabel(b.category)} ${formatNumber(b.value)}`).join(' · ')}
        {pending > 0 ? ` · ${pendingLabel} ${formatNumber(pending)}` : ''}
      </Text>
    </Flex>
  );
}

function SubjectLink({ row }: { row: AgentTimeRow }) {
  const navigate = useNavigate();
  const href = subjectHref(row);
  if (!href) return <Text>{row.label}</Text>;
  return (
    <a
      href={href}
      className="text-primary hover:underline"
      onClick={(event) => {
        event.preventDefault();
        navigate({ href });
      }}
    >
      {row.label}
    </a>
  );
}

const agentTimeColumns: ColumnDef<StockFeatures, AgentTimeRow>[] = [
  {
    id: 'label',
    header: 'What',
    cell: ({ row }) => <SubjectLink row={row.original} />,
    enableSorting: false,
    meta: { flex: 2 },
  },
  {
    id: 'category',
    header: 'Type',
    cell: ({ row }) => <TagBadge name={categoryLabel(row.original.category)} color="gray" hideDot />,
    enableSorting: false,
    meta: { flex: 1 },
  },
  {
    id: 'seconds',
    header: 'Agent time',
    cell: ({ row }) => <Text className="tabular-nums">{formatSeconds(row.original.seconds)}</Text>,
    enableSorting: false,
    meta: { flex: 0.7 },
  },
];

function AgentTimeTable({ period }: { period: BillingSummary['period'] }) {
  const [page, setPage] = React.useState(1);
  const query = useAgentTime(period.start, period.end, page, !isEmptyPeriod(period));
  const pagination = query.data?.pagination;

  return (
    <SettingsSection
      title="Agent time"
      description="What used your agent hours this period, largest first."
    >
      {query.isError ? (
        <Text className="text-destructive">Failed to load agent time.</Text>
      ) : (
        <DataTable
          columns={agentTimeColumns}
          data={query.data?.data ?? []}
          serverPagination={
            pagination
              ? {
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  pageCount: pagination.totalPages,
                  totalItems: pagination.totalItems,
                  onPageChange: setPage,
                  isLoading: query.isFetching,
                }
              : undefined
          }
        />
      )}
    </SettingsSection>
  );
}

export function BillingSection() {
  const summaryQuery = useBillingSummary({ enabled: true });
  const summary = summaryQuery.data;

  if (summaryQuery.isPending) {
    return (
      <Flex justify="center" className="py-12">
        <Spinner />
      </Flex>
    );
  }
  if (!summary) {
    return <Text className="text-destructive">Billing and usage are not available.</Text>;
  }

  const meters = billingMeters(summary);
  const subscription = summary.subscription;

  return (
    <Box className="space-y-6">
      <SettingsSection title="Plan">
        <SettingsRow title="Plan">
          <TagBadge name={planLabel(summary)} color="blue" hideDot />
        </SettingsRow>
        <SettingsRow title="Billing period">
          <Text className="tabular-nums">
            {formatDay(summary.period.start)} to {formatDay(summary.period.end)}
          </Text>
        </SettingsRow>
        {summary.trialEndsAt && (
          <SettingsRow title="Trial ends">
            <Text className="tabular-nums">{formatDay(summary.trialEndsAt)}</Text>
          </SettingsRow>
        )}
        {summary.access === 'lapsed' && (
          <SettingsRow title="Access" description="New runs and agent work are blocked.">
            <TagBadge name="Lapsed" color="red" hideDot />
          </SettingsRow>
        )}
        {subscription && (
          <SettingsRow
            title="Subscription"
            description={subscription.cancelAtPeriodEnd ? 'Cancels at the end of the period.' : undefined}
          >
            <Text>{subscription.status ?? 'None'}</Text>
          </SettingsRow>
        )}
      </SettingsSection>

      <SettingsSection title="Usage" description="Queued and running work counts toward your limits.">
        <UsageMeter
          title="Executions"
          unit="runs"
          meter={meters.executions}
          pendingLabel="queued"
          pending={summary.executions.queued}
          breakdown={[
            { category: 'flow', value: summary.executions.byCategory.flow },
            { category: 'playwright', value: summary.executions.byCategory.playwright },
          ]}
        />
        <UsageMeter
          title="Agent hours"
          unit="h"
          meter={meters.agentHours}
          pendingLabel="in flight"
          pending={summary.agentHours.inFlight}
          breakdown={[
            { category: 'healing', value: summary.agentHours.byCategory.healing },
            { category: 'agent_testing', value: summary.agentHours.byCategory.agent_testing },
            { category: 'diagnosis', value: summary.agentHours.byCategory.diagnosis },
          ]}
        />
      </SettingsSection>

      <AgentTimeTable period={summary.period} />
    </Box>
  );
}
